import { useState, useEffect } from "react";
import { LoadingIcon } from "../../assets/icons";
import { getNonFinancialAudit } from "../../service/query/auth";
import FilterDropdown from "./FilterDropdown";
import DateRangeFilter from "./DateRangeFilter";

const deriveStatus = (row) => {
    if (row.status === "success") return "Successful";
    if (row.status === "failed") return "Failed";
    return "Pending";
};

const statusStyles = {
    Successful: "bg-[#E7F7EF] text-[#0E9F6E]",
    Failed: "bg-[#FDECEC] text-[#E02424]",
    Pending: "bg-[#F3F4F6] text-[#6B7280]",
};

const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1 px-[10px] py-[3px] rounded-full text-[12px] font-medium ${statusStyles[status] ?? statusStyles.Pending}`}>
        <span className="w-[6px] h-[6px] rounded-full bg-current" />
        {status}
    </span>
);

export default function PointsAwardAuditLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [status, setStatus] = useState("");
    const [adminName, setAdminName] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await getNonFinancialAudit({
                status: status || undefined,
                adminName: adminName || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                page,
                perPage: 50,
            });
            setLogs(res?.data?.data ?? []);
            setPagination(res?.data?.pagination ?? null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const clearFilters = () => {
        setStatus("");
        setAdminName("");
        setDateFrom("");
        setDateTo("");
    };

    return (
        <div className="pt-6 bg-white min-h-[80vh]">

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center mb-[24px]">
                <div className="flex items-center gap-2 border border-[#D0D5DD] rounded-full px-4 py-2 w-[240px]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="#98A2B3" strokeWidth="1.5" />
                        <path d="M21 21L16.65 16.65" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <input
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        placeholder="Search admin name"
                        className="text-sm outline-none w-full text-[#344054] placeholder:text-[#98A2B3]"
                    />
                </div>

                <FilterDropdown
                    label="Status"
                    value={status}
                    onChange={setStatus}
                    options={[
                        { value: "", label: "All" },
                        { value: "success", label: "Success" },
                        { value: "failed", label: "Failed" },
                    ]}
                />

                <DateRangeFilter
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={({ dateFrom, dateTo }) => {
                        setDateFrom(dateFrom);
                        setDateTo(dateTo);
                    }}
                />

                <button
                    onClick={() => { setPage(1); fetchLogs(); }}
                    className="border rounded px-4 py-2 text-sm"
                >
                    Show results
                </button>
                <button
                    onClick={clearFilters}
                    className="text-sm flex items-center gap-1 text-[#344054]"
                >
                    ✕ Clear filters
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <LoadingIcon />
                </div>
            ) : logs.length === 0 ? (
                <div className="bg-[#F5F5F5] h-[200px] flex items-center justify-center rounded-lg">
                    <p className="text-[#6D6D6D] text-[14px]">No audit log entries found</p>
                </div>
            ) : (
                <div className="rounded-md overflow-hidden border border-[#E9EAEB]">
                    <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
                        <table className="w-full text-sm text-left min-w-[1100px]">
                            <thead className="bg-[#F8FAFC] sticky top-0">
                                <tr>
                                    {["Admin name", "Admin email", "Enrolment ID", "File name", "Upload time", "Total rows", "Stored", "Rejected", "Duplicates", "Processing Status", "Action"].map((header) => (
                                        <th key={header} className="px-6 py-3 text-[12px] font-semibold text-[#101828] border-b border-[#E9EAEB] whitespace-nowrap">
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((row) => (
                                    <tr key={row.id} className="border-b border-[#E9EAEB] hover:bg-[#F9FAFB]">
                                        <td className="px-6 py-4 whitespace-nowrap">{row.admin?.name ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.admin?.email ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.admin?.enrollment_id ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.meta?.file ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.created_at}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.meta?.total_rows ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-[#0E9F6E]">{row.meta?.stored ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-[#E02424]">{row.meta?.rejected ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{row.meta?.duplicates ?? "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge status={deriveStatus(row)} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => setSelectedLog(row)}
                                                className="text-[#00305E] font-medium hover:underline text-[12px]"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && (
                        <div className="flex items-center justify-between mt-4 px-2 pb-4">
                            <p className="text-[12px] text-[#64748B]">
                                Page {pagination.current_page} of {pagination.last_page} — {pagination.total} records
                            </p>
                            {pagination.last_page > 1 && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={pagination.current_page === 1}
                                        className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                                        disabled={pagination.current_page === pagination.last_page}
                                        className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === pagination.last_page ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {selectedLog && <DetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
        </div>
    );
}

function DetailsModal({ log, onClose }) {
    const meta = log?.meta ?? {};
    const fields = [
        { label: "File name", value: meta.file ?? "-" },
        { label: "Admin name", value: log?.admin?.name ?? "-" },
        { label: "Admin email", value: log?.admin?.email ?? "-" },
        { label: "Total rows", value: meta.total_rows ?? "-" },
        { label: "Stored", value: meta.stored ?? "-" },
        { label: "Rejected", value: meta.rejected ?? "-" },
        { label: "Duplicates", value: meta.duplicates ?? "-" },
        { label: "Already processed", value: meta.already_processed ?? "-" },
        { label: "Upload ID", value: meta.upload_id ?? "-" },
        { label: "Processing status", value: log.status ?? "-" },
        { label: "Time uploaded", value: log?.created_at ?? "-" },
    ];

    return (
        <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-[500px] shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-[21px]">
                    <h3 className="text-[16px] font-semibold text-[#212121]">Upload Details</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-black text-xl">✕</button>
                </div>
                <div className="space-y-[8px]">
                    {fields.map((item) => (
                        <div key={item.label} className="flex justify-between items-center p-[16px] bg-[#F9F9F9] rounded-[12px]">
                            <span className="text-[14px] text-[#5A5A5A]">{item.label}</span>
                            <span className="text-[14px] text-[#212121] font-medium text-right ml-4">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}